<script lang="ts">
  import { createContext } from 'reka-ui'
  import type {
    AiExtractDataRequestPayload,
    AiExtractDataResponse,
    ExtractionTarget
  } from '@gat-ship/types'
  import { ref, useTemplateRef } from 'vue'
  import {
    SMART_PASTE_PROVIDER_KEY,
    type SmartPasteProviderContext,
    type ShowSmartPasteDialogParams
  } from '@gat-ship/types'
  import SmartPasteDialog from './modals/smart-paste-dialog/smart-paste-dialog.vue'
  import SmartPasteStatus from './smart-paste-status.vue'
  import { useAiApiCalls } from '@gat-ship/lib/http-client'
  import { useAppLoading } from '@gat-ship/vue-components'

  export type SmartPasteProviderEmits = {
    (e: 'data-extracted', data: AiExtractDataResponse): void
  }

  export type SmartPasteProviderProps = {
    target: ExtractionTarget
  }

  export const smartPasteLoadingIds = {
    smartPaste: 'smart-paste-extract'
  }

  function defineSmartPasteState(props: SmartPasteProviderProps) {
    /** The target schema ID to extract data from */
    const target = props.target

    const smartPastePayload = ref<AiExtractDataResponse | undefined>(undefined)

    function setSmartPastePayload(payload: AiExtractDataResponse) {
      smartPastePayload.value = payload
    }
    return {
      smartPastePayload,
      set: setSmartPastePayload,
      target
    }
  }

  function defineSmartPasteCommands(
    smartPasteState: ReturnType<typeof defineSmartPasteState>,
    emit: SmartPasteProviderEmits
  ) {
    const { extractDataRequest } = useAiApiCalls()

    const { setLoadingId, removeLoadingId } = useAppLoading()

    async function fetchSmartPasteData(payload: AiExtractDataRequestPayload) {
      setLoadingId(smartPasteLoadingIds.smartPaste)
      try {
        const response = await extractDataRequest(payload)
        if (response && response.data) {
          smartPasteState.set(response.data)
          emit('data-extracted', response.data)
        }
        return response
      } finally {
        removeLoadingId(smartPasteLoadingIds.smartPaste)
      }
    }

    return {
      fetchSmartPasteData
    }
  }

  function defineSmartPasteContext(
    props: SmartPasteProviderProps,
    emit: SmartPasteProviderEmits
  ): Omit<SmartPasteProviderContext, 'modals'> {
    const smartPasteState = defineSmartPasteState(props)
    const commands = defineSmartPasteCommands(smartPasteState, emit)
    return {
      smartPasteData: smartPasteState.smartPastePayload,
      commands
    }
  }

  const [useSmartPaste, provideSmartPaste] =
    createContext<SmartPasteProviderContext>(
      SMART_PASTE_PROVIDER_KEY.toString()
    )

  export { useSmartPaste }
</script>

<script setup lang="ts">
  const props = defineProps<SmartPasteProviderProps>()
  const emit = defineEmits<SmartPasteProviderEmits>()

  const smartPasteContext = defineSmartPasteContext(props, emit)

  const smartPasteDialog = useTemplateRef('smart-paste-dialog')

  provideSmartPaste({
    ...smartPasteContext,
    modals: {
      showSmartPasteDialog: (params: ShowSmartPasteDialogParams) => {
        smartPasteDialog.value?.showSmartPasteDialog(params)
      }
    }
  })
</script>
<template>
  <slot />
  <smart-paste-dialog ref="smart-paste-dialog" />
  <smart-paste-status />
</template>
